import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../components/common/Button';
import { mockDebates, mockTurns } from '../utils/mockData';
import { Debate, Turn, DebateResult } from '../types';

const Results: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [debate, setDebate] = useState<Debate | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [showFullTranscript, setShowFullTranscript] = useState(false);
  
  // Mock result data
  const mockResult: DebateResult = {
    debateId: id || '',
    winner: 'B',
    votePercentage: {
      A: 45,
      B: 55
    },
    totalVotes: 1234,
    keyArguments: {
      sideA: [
        'International treaties provide necessary oversight for AI development',
        'Historical precedents show regulation can enhance rather than hinder innovation',
        'Existential risks of unregulated AI outweigh competitive advantages'
      ],
      sideB: [
        'Innovation requires freedom from bureaucratic constraints',
        'Industry-led standards are more flexible and effective than treaties',
        'Bad actors will ignore treaties, putting compliant nations at disadvantage'
      ]
    }
  };
  
  useEffect(() => {
    const foundDebate = mockDebates.find(d => d.id === id);
    if (foundDebate) {
      setDebate(foundDebate);
      const debateTurns = mockTurns.filter(t => t.debateId === id);
      setTurns(debateTurns);
    }
  }, [id]);
  
  if (!debate) {
    return (
      <div className="blueprint-grid flex min-h-screen items-center justify-center bg-white">
        <p className="font-serif text-2xl text-charcoal">Results not found</p>
      </div>
    );
  }
  
  const winnerSide = mockResult.winner === 'A' ? debate.sideA : debate.sideB;
  const winnerColor = mockResult.winner === 'A' ? 'bg-steel-blue' : 'bg-vermilion';
  
  return (
    <div className="relative isolate min-h-screen bg-white py-10">
      <div aria-hidden className="blueprint-grid pointer-events-none absolute inset-0 z-0" />
      <div className="relative z-10 mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        {/* Winner Banner */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`${winnerColor} mb-8 rounded-2xl p-8 text-center text-white`}
        >
          <p className="font-mono text-[11px] uppercase tracking-eyebrow text-white/70">Verdict</p>
          <h1 className="mt-2 font-serif text-3xl">
            Side {mockResult.winner} wins
          </h1>
          <p className="mt-1 text-lg text-white/85">
            {winnerSide.position}
          </p>
        </motion.div>
        
        {/* Topic */}
        <div className="card-frame mb-8 p-6">
          <h2 className="font-serif text-2xl text-charcoal">
            {debate.topic}
          </h2>
          {debate.description && <p className="mt-2 text-sm text-ink-muted">{debate.description}</p>}
        </div>
        
        {/* Vote Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card-frame mb-8 p-6"
        >
          <h3 className="text-xl font-serif font-bold text-charcoal mb-6">
            Vote Breakdown
          </h3>
          
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium text-charcoal flex items-center">
                  <span className="w-8 h-8 bg-steel-blue text-white rounded-full flex items-center justify-center mr-2 text-sm font-bold">
                    A
                  </span>
                  {debate.sideA.position}
                </span>
                <span className="font-bold text-charcoal">
                  {mockResult.votePercentage.A}%
                </span>
              </div>
              <div className="h-3 w-full overflow-hidden rounded-full bg-hairline">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${mockResult.votePercentage.A}%` }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                  className="h-3 rounded-full bg-steel-blue"
                />
              </div>
            </div>
            
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium text-charcoal flex items-center">
                  <span className="w-8 h-8 bg-vermilion text-white rounded-full flex items-center justify-center mr-2 text-sm font-bold">
                    B
                  </span>
                  {debate.sideB.position}
                </span>
                <span className="font-bold text-charcoal">
                  {mockResult.votePercentage.B}%
                </span>
              </div>
              <div className="h-3 w-full overflow-hidden rounded-full bg-hairline">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${mockResult.votePercentage.B}%` }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  className="h-3 rounded-full bg-vermilion"
                />
              </div>
            </div>
          </div>
          
          <p className="text-center text-ink-muted mt-6">
            Total votes: {mockResult.totalVotes.toLocaleString()}
          </p>
        </motion.div>
        
        {/* Key Arguments */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card-frame mb-8 p-6"
        >
          <h3 className="text-xl font-serif font-bold text-charcoal mb-6">
            Key Arguments
          </h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-charcoal mb-3 flex items-center">
                <span className="w-6 h-6 bg-steel-blue text-white rounded-full flex items-center justify-center mr-2 text-xs font-bold">
                  A
                </span>
                Side A
              </h4>
              <ul className="space-y-2">
                {mockResult.keyArguments.sideA.map((arg, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-steel-blue mr-2 text-lg">•</span>
                    <span className="text-charcoal/80">{arg}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-charcoal mb-3 flex items-center">
                <span className="w-6 h-6 bg-vermilion text-white rounded-full flex items-center justify-center mr-2 text-xs font-bold">
                  B
                </span>
                Side B
              </h4>
              <ul className="space-y-2">
                {mockResult.keyArguments.sideB.map((arg, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-vermilion mr-2 text-lg">•</span>
                    <span className="text-charcoal/80">{arg}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>
        
        {/* Full Transcript */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="card-frame mb-8 p-6"
        >
          <button
            onClick={() => setShowFullTranscript(!showFullTranscript)}
            className="w-full flex justify-between items-center text-left"
          >
            <h3 className="text-xl font-serif font-bold text-charcoal">
              Full Transcript
            </h3>
            <motion.span
              animate={{ rotate: showFullTranscript ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <svg className="w-5 h-5 text-ink-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </motion.span>
          </button>
          
          <AnimatePresence>
            {showFullTranscript && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="mt-6 space-y-4">
                  {turns.map(turn => (
                    <div key={turn.id} className="border-l-4 pl-4" style={{
                      borderColor: turn.side === 'A' ? '#3A68A0' : '#E5442D'
                    }}>
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-semibold text-charcoal">
                          Side {turn.side} - {turn.type.charAt(0).toUpperCase() + turn.type.slice(1)}
                        </span>
                        <span className="text-sm text-ink-muted">
                          {new Date(turn.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                      <p className="text-charcoal/80">{turn.content}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
        
        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={() => navigate('/explore')}
            variant="secondary"
          >
            View similar debates
          </Button>
          <Button
            onClick={() => {
              // Share functionality
              if (navigator.share) {
                navigator.share({
                  title: debate.topic,
                  text: `Check out this debate: ${debate.topic}`,
                  url: window.location.href
                });
              }
            }}
          >
            Share results
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Results;
